import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { routesForContext } from "JS/Routing";
import { config as appConfig } from "JS/Config";
import { clearLocalStorage } from "JS/Helpers";
import { store } from "JS/Redux/Store";
import { resetCourseState } from "JS/Redux/Course";
import { resetEventsState } from "JS/Redux/Event";
import { softResetGiftState } from "JS/Redux/Gift";
import { resetHomeState } from "JS/Redux/Home";
import { resetWebcastState } from "JS/Redux/Webcast";
import { resetMediaState } from "JS/Redux/media";
import { resetNavStack } from "JS/Redux/NavStack";
import { resetRecommendationsState } from "JS/Redux/Recommendations";
import { resetMediaEssentials } from "JS/Redux/MediaEssentials";
import {
    resetGlobalDownloadClickCount,
    resetGlobalDownloadCount,
} from "JS/Redux/DownloadCount";
import {
    deleteDeviceTokenApi,
    getLocalFcmToken,
} from "JS/Helpers/PushNotifications";
import { updateNgTokens } from "JS/Helpers/LocalStorageHelpers";
import { appConstants } from "JS/Helpers/Constants";
import { resetAudioState } from "JS/Redux/Audio";
import { resetVideoState } from "JS/Redux/Video";
import { resetPlaylistResumeMap } from "JS/Redux/Playlist";
import { resetMixedContentState } from "JS/Redux/MixedContent";
import { resetRedirectionState } from "JS/Redux/ConsentForm";
import { resetBundleOptIn } from "JS/Redux/BundleOptIn";
import { resetBundleSubscription } from "JS/Redux/BundleSubscription";
import { resetPurchaseBundle } from "JS/Redux/PurchaseBundle";
import { resetPaymentEligibleUsers } from "JS/Redux/PaymentEligibleUsers";

export const logoutUser = async () => {
    const token = getLocalFcmToken();
    if (token) {
        try {
            await deleteDeviceTokenApi(appConfig.user.memberId, token);
        } catch (err) {}
    }

    clearLocalStorage();
    store.dispatch(resetAudioState());
    store.dispatch(resetVideoState());
    store.dispatch(resetPlaylistResumeMap());
    store.dispatch(resetCourseState());
    store.dispatch(resetEventsState());
    store.dispatch(softResetGiftState());
    store.dispatch(resetHomeState());
    store.dispatch(resetWebcastState());
    store.dispatch(resetMediaState());
    store.dispatch(resetNavStack());
    store.dispatch(resetRecommendationsState());
    store.dispatch(resetMixedContentState());
    store.dispatch(resetRedirectionState());
    store.dispatch(resetPurchaseBundle());
    store.dispatch(resetBundleOptIn());
    store.dispatch(resetBundleSubscription());
    if (store?.getState()?.mediaEssentials?.download_warning_cleared_on_logout)
        store.dispatch(resetGlobalDownloadCount());
    store.dispatch(resetGlobalDownloadClickCount());
    store.dispatch(resetPaymentEligibleUsers());
    store.dispatch(resetMediaEssentials());
    setTimeout(() => {
        window.location.reload();
    }, 500);
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};
export class BaseService {
    private cmlmManager: AxiosInstance;
    private ngManager: AxiosInstance;
    protected routes = routesForContext()();

    constructor() {
        this.ngManager = this.createAxiosInstance(appConfig.ngBaseUrl);
    }

    private createAxiosInstance(baseURL: string): AxiosInstance {
        const instance = axios.create({
            headers: {
                Accept: "application/json",
                "x-api-key": appConfig.xApiKey,
                "x-tenant-id": appConfig.xTenantId,
            },
            baseURL,
        });

        instance?.interceptors?.request?.use((config) => {
            if (appConfig.ngAccesstoken) {
                config.headers[
                    "Authorization"
                ] = `Bearer ${appConfig.ngAccesstoken}`;
            }
            return config;
        });

        instance?.interceptors?.response?.use(
            (res) => res,
            async (err) => {
                const originalRequest = err.config;

                if (!err.response) return Promise.reject(err);

                const isAuthError = (status) =>
                    status === 403 && !originalRequest._retry;

                const updateRequestWithToken = (request, token) => {
                    request.headers["authorization"] = `Bearer ${token}`;
                };

                const handleQueue = (token) => {
                    failedQueue.forEach((promis) => promis.resolve(token));
                    failedQueue = [];
                };

                const handleFailure = async () => {
                    isRefreshing = false;
                    processQueue(err, null);

                    logoutUser();
                    return Promise.reject(err);
                };

                if (isAuthError(err.response.status)) {
                    if (isRefreshing) {
                        return new Promise((resolve, reject) => {
                            failedQueue.push({ resolve, reject });
                        })
                            .then((token) => {
                                updateRequestWithToken(originalRequest, token);
                                originalRequest._retry = true;
                                return instance(originalRequest);
                            })
                            .catch(Promise.reject);
                    }

                    originalRequest._retry = true;
                    isRefreshing = true;

                    try {
                        const res = await instance.post(
                            appConstants.ngEndpoints.refreshToken,
                            {
                                refreshToken: appConfig?.ngRefreshToken,
                                memberId: appConfig?.user?.memberId,
                                tenantId: "ltd",
                                clientId: 2,
                                scope: "media_scope",
                                platform: "web",
                            },
                        );

                        const resData = res?.data;

                        if (resData?.status && resData?.data) {
                            const { accessToken, refreshToken } = resData.data;
                            updateNgTokens(accessToken, refreshToken);

                            handleQueue(accessToken);
                            updateRequestWithToken(
                                originalRequest,
                                accessToken,
                            );

                            return instance(originalRequest);
                        }

                        return handleFailure();
                    } catch (err) {
                        return handleFailure();
                    } finally {
                        isRefreshing = false;
                    }
                }

                return Promise.reject(err);
            },
        );

        return instance;
    }

    protected async ngCall<T>(
        method: "get" | "post" | "put" | "delete" | "patch",
        url: string,
        data?: any,
        reqConfig?: AxiosRequestConfig,
    ): Promise<AxiosResponse<T>> {
        try {
            switch (method) {
                case "get":
                    return this.ngManager.get<T>(url, {
                        ...data,
                        ...reqConfig,
                    });
                case "post":
                    return this.ngManager.post<T>(url, data, reqConfig);
                case "put":
                    return this.ngManager.put<T>(url, data, reqConfig);
                case "delete":
                    return this.ngManager.delete<T>(url, {
                        ...data,
                        ...reqConfig,
                    });
                case "patch":
                    return this.ngManager.patch<T>(url, {
                        ...data,
                        ...reqConfig,
                    });
                default:
                    throw new Error(`Unsupported method: ${method}`);
            }
        } catch (err) {
            throw err;
        }
    }
}

export const routes = routesForContext()();
const axiosInstance = axios.create({
    headers: {
        Accept: "application/json",
        "x-api-key": appConfig.xApiKey,
        "x-tenant-id": appConfig.xTenantId,
    },
    baseURL: appConfig.ngBaseUrl,
});
export function ngDeleteCall<T>(
    url: string,
    data?: any,
    reqConfig?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
    try {
        return axiosInstance.delete<T>(url, {
            ...reqConfig,
            data,
            headers: {
                authorization: `Bearer ${appConfig.ngAccesstoken}`,
            },
        });
    } catch (err) {
        throw err;
    }
}
